import { useCallback } from 'react';
import { create } from 'zustand';
import { cabinet } from '../api/cabinet';
import { DEFAULT_LOCALE } from '../config';
import { safeStorage } from '../lib/storage';
import { en } from './en';
import { pl } from './pl';
import { ro } from './ro';
import { ru } from './ru';
import { uk, type Dict, type DictKey } from './uk';

/**
 * Мови кабінету: п'ять вбудованих словників (джерело ключів і запасний варіант) + переклади з БД
 * (`GET /i18n/cabinet`, простір імен `cabinet` у ui_translations, редагується в CRM → Налаштування → Мови).
 * Мова, додана в CRM (напр. `de` з хоча б одним рядком), з'являється в меню без змін коду.
 *
 * Пошук рядка: БД обрана мова → БД uk → вбудована обрана мова → вбудована uk → сам ключ.
 * Вибір мови: обрана на пристрої → мова контакту → мова сайту справи → мова сайту → пристрій → збірка → uk.
 */

/** Built-in languages (bundled dictionaries). */
export const LOCALES = [
  { code: 'uk', name: 'Українська' },
  { code: 'en', name: 'English' },
  { code: 'pl', name: 'Polski' },
  { code: 'ro', name: 'Română' },
  { code: 'ru', name: 'Русский' },
] as const;

export type BuiltinLocale = (typeof LOCALES)[number]['code'];
/** Any language code: a built-in one or one added at runtime in the CRM. */
export type Locale = string;
export const DICTS: Record<BuiltinLocale, Dict> = { uk, en, pl, ro, ru };
export const FALLBACK: BuiltinLocale = 'uk';
export const NAMESPACE = 'cabinet';
const KEY = 'xq.cabinet.locale';

export type Vars = Record<string, string | number>;
/** `{locale: {key: text}}` from the DB. */
export type Strings = Record<string, Record<string, string>>;
export interface LocaleInfo {
  code: string;
  name: string;
  position?: number;
}

export const BUILTIN_LOCALES: LocaleInfo[] = LOCALES.map((l, i) => ({ code: l.code, name: l.name, position: i }));

/** One of the five bundled dictionaries. */
export function isLocale(v: unknown): v is BuiltinLocale {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(DICTS, v);
}

/** "uk-UA" → "uk", "mo"/"ro-MD" → "ro", "ua" → "uk", "de" → "de". Any `^[a-z]{2,3}$` base code; otherwise null. */
export function normalizeLocale(v: string | null | undefined): Locale | null {
  if (!v) return null;
  const base = v.trim().toLowerCase().split(/[-_]/)[0] ?? '';
  if (base === 'mo') return 'ro';
  if (base === 'ua') return 'uk';
  return /^[a-z]{2,3}$/.test(base) ? base : null;
}

export function format(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

const nonEmpty = (v: string | undefined): string | undefined => (typeof v === 'string' && v.trim() ? v : undefined);

/** DB chosen → DB uk → built-in chosen → built-in uk → key. Empty strings count as missing. */
export function resolveString(strings: Strings, locale: string, key: string): string {
  const builtin = isLocale(locale) ? (DICTS[locale] as Record<string, string>) : undefined;
  return (
    nonEmpty(strings[locale]?.[key]) ??
    nonEmpty(strings[FALLBACK]?.[key]) ??
    nonEmpty(builtin?.[key]) ??
    nonEmpty((uk as Record<string, string>)[key]) ??
    key
  );
}

export function translate(locale: Locale, key: DictKey | string, vars?: Vars, strings: Strings = {}): string {
  return format(resolveString(strings, locale, key), vars);
}

/** First candidate that is an available language; otherwise uk. */
export function pickLocale(available: readonly string[], candidates: (string | null | undefined)[]): Locale {
  for (const c of candidates) {
    const n = normalizeLocale(c);
    if (n && available.includes(n)) return n;
  }
  return FALLBACK;
}

export interface LocaleSources {
  /** What the person picked in the language menu (remembered on this device). Wins over everything. */
  chosen: Locale | null;
  /** contact.locale from /auth/me. */
  contact: Locale | null;
  /** default_locale of the site of the case being viewed (MD case → ro). */
  caseSite?: Locale | null;
  /** default_locale of the country site from /public/site. */
  site: Locale | null;
  /** navigator.language. */
  device?: string | null;
}

const BUILTIN_CODES = LOCALES.map((l) => l.code) as string[];

/** chosen → contact → case site → site → device → build default → uk (only languages that are available). */
export function resolveLocale(s: LocaleSources, available: readonly string[] = BUILTIN_CODES): Locale {
  return pickLocale(available, [s.chosen, s.contact, s.caseSite, s.site, s.device, DEFAULT_LOCALE]);
}

/**
 * Languages for the menu: built-in ones are always available, a DB language once it has ≥1 string in `cabinet`.
 * Names come from the API (ui_locales), built-in names are the fallback. Order: API position, then code.
 */
export function availableLocales(apiLocales: LocaleInfo[] | null | undefined, strings: Strings): LocaleInfo[] {
  const out = new Map<string, LocaleInfo>();
  for (const l of apiLocales ?? []) {
    const code = normalizeLocale(l.code);
    if (!code || out.has(code)) continue;
    const hasTexts = Object.values(strings[code] ?? {}).some((v) => nonEmpty(v));
    if (!isLocale(code) && !hasTexts) continue;
    const builtinName = BUILTIN_LOCALES.find((b) => b.code === code)?.name;
    out.set(code, { code, name: (l.name && l.name.trim()) || builtinName || code, position: l.position });
  }
  const tail = (apiLocales?.length ? Math.max(...apiLocales.map((l) => l.position ?? 0)) : 0) + 1;
  for (const b of BUILTIN_LOCALES) if (!out.has(b.code)) out.set(b.code, { ...b, position: tail + (b.position ?? 0) });
  return [...out.values()].sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.code.localeCompare(b.code));
}

/** Move `first` (e.g. the case site's ro/ru) to the top, keeping their order; the rest keep theirs. */
export function orderLocales(locales: LocaleInfo[], first: readonly string[] | null | undefined): LocaleInfo[] {
  const pref = (first ?? []).map((c) => normalizeLocale(c)).filter((c): c is string => !!c);
  if (!pref.length) return locales;
  const top = pref.map((c) => locales.find((l) => l.code === c)).filter((l): l is LocaleInfo => !!l);
  return [...top, ...locales.filter((l) => !pref.includes(l.code))];
}

/** One built-in dictionary as a flat `{key: text}` (sorted keys) — what scripts/export-i18n.mjs writes. */
export function exportBuiltin(locale: BuiltinLocale = FALLBACK): Record<string, string> {
  return Object.fromEntries(Object.entries(DICTS[locale]).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)));
}

interface I18nState extends Required<Omit<LocaleSources, 'device'>> {
  locale: Locale;
  locales: LocaleInfo[];
  strings: Strings;
  loaded: boolean;
  setLocale: (l: Locale) => void;
  setContactLocale: (l: string | null | undefined) => void;
  setSiteLocale: (l: string | null | undefined) => void;
  setCaseSiteLocale: (l: string | null | undefined) => void;
  /** GET /i18n/cabinet. Failures keep what we have (built-in dictionaries). */
  load: () => Promise<void>;
}

function deviceLocale(): string | null {
  try {
    return globalThis.navigator?.language ?? null;
  } catch {
    return null;
  }
}

const saved = normalizeLocale(safeStorage.get(KEY));
const codes = (ls: LocaleInfo[]) => ls.map((l) => l.code);

export const useI18n = create<I18nState>((set, get) => {
  type P = Partial<Pick<I18nState, 'chosen' | 'contact' | 'site' | 'caseSite' | 'locales' | 'strings' | 'loaded'>>;
  const update = (p: P) => {
    const n = { ...get(), ...p };
    set({ ...p, locale: resolveLocale({ ...n, device: deviceLocale() }, codes(n.locales)) });
  };
  return {
    chosen: saved,
    contact: null,
    site: null,
    caseSite: null,
    locales: BUILTIN_LOCALES,
    strings: {},
    loaded: false,
    locale: resolveLocale({ chosen: saved, contact: null, site: null, device: deviceLocale() }),
    setLocale: (l) => {
      const n = normalizeLocale(l);
      if (!n) return;
      safeStorage.set(KEY, n);
      update({ chosen: n });
    },
    setContactLocale: (l) => update({ contact: normalizeLocale(l) }),
    setSiteLocale: (l) => update({ site: normalizeLocale(l) }),
    setCaseSiteLocale: (l) => {
      const n = normalizeLocale(l);
      if (n !== get().caseSite) update({ caseSite: n });
    },
    async load() {
      try {
        const r = await cabinet.i18n(NAMESPACE);
        const strings = r?.strings && typeof r.strings === 'object' ? r.strings : {};
        update({ strings, locales: availableLocales(r?.locales, strings), loaded: true });
      } catch {
        set({ loaded: true });
      }
    },
  };
});

/** Non-hook access (stores). */
export function t(key: DictKey, vars?: Vars): string {
  const s = useI18n.getState();
  return translate(s.locale, key, vars, s.strings);
}

/** Hook: `const t = useT(); t('login.title')`. Re-renders when the language or the DB strings change. */
export function useT() {
  const locale = useI18n((s) => s.locale);
  const strings = useI18n((s) => s.strings);
  return useCallback((key: DictKey, vars?: Vars) => translate(locale, key, vars, strings), [locale, strings]);
}

export type T = ReturnType<typeof useT>;
export type { DictKey };
